/* eslint-disable @typescript-eslint/no-misused-promises */
import { useFieldArray, useForm } from "react-hook-form";
import {
  milestoneOfferForm,
  MilestoneOfferForm as MilestoneOfferFormSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { CurrencyInput, Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

import { NewOffer } from "@/types";
import { Trash } from "lucide-react";

interface Props {
  sender: "company" | "candidate";
  candidateName: string | undefined;
  isPending: boolean;
  error: Error | null;
  isCounter?: boolean;
  mutate: (data: NewOffer) => void;
  onCancel: () => void;
}

const MilestoneOfferForm = ({
  sender,
  isPending,
  error,
  isCounter,
  candidateName = "the candidate",
  mutate,
  onCancel,
}: Props) => {
  const form = useForm<MilestoneOfferFormSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(milestoneOfferForm)(data, context, opts);
    },
    defaultValues: {
      terms: [
        {
          milestoneLabel: "",
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "terms",
  });

  function onSubmit(data: MilestoneOfferFormSchema) {
    if (isPending) return;

    mutate({
      terms: data.terms.map((term) => ({
        termType: "milestone",
        milestoneLabel: term.milestoneLabel,
        totalAmountCents: term.totalAmountCents,
      })),
      note: data.note,
    });
  }

  return (
    <Form {...form}>
      {error && (
        <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
          Something went wrong when making a counter offer, please try again
          later.
        </p>
      )}
      <form className="space-y-4 w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div className="p-4 mb-4 bg-blue-50 border border-blue-100 rounded-lg">
            <p className="text-sm text-gray-700 leading-relaxed">
              {sender === "company"
                ? `This is a milestone-based engagement. Add milestones where you'll
              compensate ${candidateName} for their work. 
              ${candidateName.at(0)?.toUpperCase() + candidateName.slice(1)} will mark each
              milestone as complete in Venturous, triggering a notification for
              you to make a payment. Milestones can include project kickoff,
              deliverable submissions, project completion, or any agreed-upon
              checkpoint.`
                : `This is a milestone-based engagement. Add milestones where the company
                will compensate you for your work. You will mark each
              milestone as complete in Venturous, triggering a notification for
              you to make a payment. Milestones can include project kickoff,
              deliverable submissions, project completion, or any agreed-upon
              checkpoint.`}
            </p>
          </div>
          {fields.map((item, index) => (
            <div
              key={item.id}
              className="border border-gray-200 rounded-lg p-4 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">
                  Milestone {index + 1}
                </h3>

                {index > 0 && (
                  <Button variant="ghostWithIcon" onClick={() => remove(index)}>
                    <Trash />
                  </Button>
                )}
              </div>

              <FormField
                control={form.control}
                name={`terms.${index}.milestoneLabel`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Milestone label</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Milestone name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`terms.${index}.totalAmountCents`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Payment Amount</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        valueCents={field.value}
                        onValueCentsChange={field.onChange}
                        minCents={0}
                        placeholder="10,000"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          ))}
          <Button
            variant="ghost"
            type="button"
            onClick={() => append({ milestoneLabel: "", totalAmountCents: 0 })}
          >
            + Add milestone
          </Button>
        </div>
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Note to the {sender === "company" ? "candidate" : "employer"}{" "}
                (optional)
              </FormLabel>
              <FormControl>
                <Textarea {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className="!mb-1 flex justify-between">
          <Button variant="stroke" className="mt-2" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="success"
            type="submit"
            className="mt-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(onSubmit)}
          >
            {isCounter ? "Submit Counter Offer" : "Submit Offer"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default MilestoneOfferForm;
